import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { ArrowRight, Car, Package, ArrowLeft } from "lucide-react";

/**
 * Public-facing marketing page: a deep look at everything Link Ride does.
 *
 * Data is pulled from /api/features-catalog so the page never drifts from the
 * server-side source-of-truth (services/feature_catalog.py). On the off-chance
 * the API fails we surface a clean fallback instead of an empty screen.
 *
 * Design intent: NOT generic "AI slop." Asymmetric left-aligned hero, oversized
 * stat numbers, branded color rails per section, no purple-on-white gradients.
 */

const FALLBACK_COUNTS = { sections: 4, groups: 20, features: 112 };

export default function FeaturesCatalog() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    document.title = "Link Ride · What it does";
    let alive = true;
    axios.get(`${API}/features-catalog`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, []);

  const counts = data?.counts || FALLBACK_COUNTS;
  const sections = data?.sections || [];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#0047FF] selection:text-white">
      {/* Sticky top bar — keep the brand visible while users scroll the catalog */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0a0a0a]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="features-back-home">
            <ArrowLeft className="w-4 h-4 text-zinc-500 group-hover:text-white transition-colors" />
            <span className="text-xl font-bold tracking-tight">Link Ride</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/become-driver"
              className="hidden sm:inline-flex text-xs text-zinc-400 hover:text-white px-3 py-2 rounded-full transition-colors"
              data-testid="features-cta-driver"
            >
              Drive with us
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 bg-[#0047FF] hover:bg-[#003BE0] text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors"
              data-testid="features-cta-book"
            >
              Book a ride <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12 sm:pt-24 sm:pb-20">
        <p className="text-xs uppercase tracking-[0.25em] text-[#4D88FF] font-bold mb-6" data-testid="features-eyebrow">
          The full picture
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight max-w-3xl">
          Everything Link Ride<br />actually does.
        </h1>
        <p className="mt-6 text-base text-zinc-400 max-w-xl leading-relaxed">
          A locally-owned rideshare &amp; same-day delivery platform serving Reading, West Reading, Wyomissing, and the surrounding area. This page lists every customer, driver, admin, and automation feature shipped to production.
        </p>
        <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[11px] uppercase tracking-[0.15em] font-bold text-emerald-300" data-testid="features-freshness">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Live · synced with production
        </div>

        {/* Asymmetric stats row — big number on left, smaller on right */}
        <div className="mt-14 grid grid-cols-3 gap-6 sm:gap-12 max-w-xl">
          <StatBlock value={counts.features} label="features shipped" big data-testid="stat-features" />
          <StatBlock value={counts.groups}   label="categories"      data-testid="stat-groups" />
          <StatBlock value={counts.sections} label="user surfaces"   data-testid="stat-sections" />
        </div>
      </section>

      {/* Section cards */}
      <main className="max-w-6xl mx-auto px-6 pb-24 space-y-16">
        {err && sections.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="features-error">
            Couldn&apos;t load the live catalog right now. <Link to="/" className="underline">Head back home</Link> and try again in a moment.
          </p>
        ) : (
          sections.map((s, idx) => (
            <SectionBlock key={s.title} section={s} index={idx} />
          ))
        )}
      </main>

      {/* Closing CTA — the only "AI slop" risk on this page so we keep it tight */}
      <section className="border-t border-white/5 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">Ready when you are.</h2>
          <p className="text-zinc-400 max-w-lg mb-10 leading-relaxed">
            Whether you need a ride across town, a package delivered same-day, or you want to drive on your own schedule — Link Ride is built right here in Berks County.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#0047FF] hover:bg-[#003BE0] text-white font-semibold px-6 py-3.5 rounded-full transition-colors"
              data-testid="features-footer-cta-book"
            >
              <Car className="w-4 h-4" /> Book a ride
            </Link>
            <Link
              to="/become-driver"
              className="inline-flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 font-semibold px-6 py-3.5 rounded-full border border-emerald-500/30 transition-colors"
              data-testid="features-footer-cta-driver"
            >
              <Package className="w-4 h-4" /> Become a driver
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-zinc-600">
          <span>Link Ride &middot; West Reading, PA &middot; link@linklitepro.com</span>
          <span>This page reflects the live production build.</span>
        </div>
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function StatBlock({ value, label, big = false, ...rest }) {
  return (
    <div {...rest}>
      <div className={
        big
          ? "text-5xl sm:text-7xl font-bold tracking-tight text-white leading-none"
          : "text-3xl sm:text-5xl font-bold tracking-tight text-zinc-300 leading-none"
      }>
        {value}
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-500">
        {label}
      </div>
    </div>
  );
}

function SectionBlock({ section, index }) {
  const color = section.color || "#0047FF";
  return (
    <section data-testid={`features-section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}>
      {/* Section header — vertical color rail + huge title on left, subtitle below */}
      <div className="flex items-start gap-5 mb-10">
        <div
          className="w-1 self-stretch rounded-full mt-1 shrink-0"
          style={{ background: color, minHeight: "60px" }}
          aria-hidden
        />
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-2" style={{ color }}>
            0{index + 1} &middot; Surface
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{section.title}</h2>
          <p className="mt-2 text-sm text-zinc-400 max-w-xl leading-relaxed">{section.subtitle}</p>
        </div>
      </div>

      {/* Asymmetric 2-col grid — odd groups span full width on mobile, alternating */}
      <div className="grid gap-x-10 gap-y-12 sm:grid-cols-2">
        {section.groups.map((g) => (
          <GroupBlock key={g.name} group={g} accent={color} />
        ))}
      </div>
    </section>
  );
}

function GroupBlock({ group, accent }) {
  return (
    <div className="group">
      <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-zinc-500 mb-4 pb-3 border-b border-white/5">
        {group.name}
      </h3>
      <ul className="space-y-3">
        {group.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-[13.5px] text-zinc-300 leading-relaxed">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full mt-2 shrink-0"
              style={{ background: accent }}
              aria-hidden
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
